import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CalendarMonth as CalendarMonthIcon,
  InfoOutlined as InfoIcon,
  Sync as SyncIcon,
  Block as BlockIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import facturaCompraService, { FacturaCompra } from '../../services/facturaCompraService';
import variablesSistemaService from '../../services/variablesSistemaService';
import { formatCurrency, formatDateLocal } from '../../utils/formatters';
import { toFechaVencimInputValue, ymdToDdMmYyyy } from '../../utils/ordenCompraFechaProbable';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';
import { getApiErrorMessage } from '../../utils/apiError';
import PDFViewer from '../../components/shared/PDFViewer';
import ImprimirEtiquetasCodigosDialog from '../../components/compras/ImprimirEtiquetasCodigosDialog';
import AplicarSaldoReciboDialog from '../../components/Tesoreria/AplicarSaldoReciboDialog';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

/** Formato Letra-Suc-Nro: A-0001-00000001 */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

interface EdicionForm {
  nroSuc: string;
  nroComprobante: string;
  fechaEmision: string;
  fechaVencim: string;
  aplicacionIva: string;
  descripcion: string;
}

const DESCRIPCION_MAX_LENGTH = 2000;

const edicionDesdeFactura = (fc: FacturaCompra): EdicionForm => ({
  nroSuc: fc.nroSuc != null ? String(fc.nroSuc) : '',
  nroComprobante: fc.nroComprobante != null ? String(fc.nroComprobante) : '',
  fechaEmision: toFechaVencimInputValue(fc.fechaEmision),
  fechaVencim: toFechaVencimInputValue(fc.fechaVencim),
  aplicacionIva: toFechaVencimInputValue(fc.aplicacionIva) || toFechaVencimInputValue(fc.fechaEmision),
  descripcion: (fc.descripcion ?? '').trimEnd(),
});

const edicionIgual = (a: EdicionForm, b: EdicionForm): boolean =>
  Number(a.nroSuc) === Number(b.nroSuc) &&
  Number(a.nroComprobante) === Number(b.nroComprobante) &&
  a.fechaEmision === b.fechaEmision &&
  a.fechaVencim === b.fechaVencim &&
  a.aplicacionIva === b.aplicacionIva &&
  a.descripcion.trimEnd() === b.descripcion.trimEnd();

interface FechaEditableFieldProps {
  label: string;
  value: string;
  onChange: (ymd: string) => void;
  required?: boolean;
  disabled?: boolean;
}

const FechaEditableField: React.FC<FechaEditableFieldProps> = ({ label, value, onChange, required, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const el = inputRef.current;
    if (!el || disabled) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // fallback click
      }
    }
    el.click();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        required={required}
        disabled={disabled}
        error={required && !value}
        value={value ? ymdToDdMmYyyy(value) : ''}
        placeholder="DD/MM/AAAA"
        onClick={openPicker}
        inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" size="small" onClick={openPicker} disabled={disabled} aria-label="Seleccionar fecha">
                <CalendarMonthIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <input
        ref={inputRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        tabIndex={-1}
      />
    </Box>
  );
};

/** Anulada solo cuando el backend marcó explícitamente Nombre = ANULADO. */
const esFacturaAnulada = (fc: FacturaCompra): boolean =>
  (fc.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const FacturaCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const [factura, setFactura] = useState<FacturaCompra | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerandoAsiento, setRegenerandoAsiento] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [openRegenerarDialog, setOpenRegenerarDialog] = useState(false);
  const [openImpuestosDialog, setOpenImpuestosDialog] = useState(false);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [aplicarSaldoOpen, setAplicarSaldoOpen] = useState(false);
  const [edicion, setEdicion] = useState<EdicionForm | null>(null);
  const [guardandoEdicion, setGuardandoEdicion] = useState(false);
  const pedirEtiquetasPendienteRef = useRef(false);
  const [pdfEtiquetas, setPdfEtiquetas] = useState<{ url: string; fileName: string } | null>(null);
  const pdfEtiquetasRef = useRef<{ url: string; fileName: string } | null>(null);
  const [impuestoTasas, setImpuestoTasas] = useState<{
    impuestotasa1?: number;
    impuestotasa2?: number;
    impuestotasa3?: number;
    impuestotasa4?: number;
  }>({});

  const loadFactura = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (isNaN(id)) {
      enqueueSnackbar('Factura no válida', { variant: 'error' });
      navigate('/compras/facturas');
      return;
    }
    try {
      setLoading(true);
      const data = await facturaCompraService.getFacturaCompra(id);
      setFactura(data);
      setEdicion(edicionDesdeFactura(data));
    } catch (error) {
      console.error('Error al cargar factura:', error);
      enqueueSnackbar('Error al cargar la factura de compra', { variant: 'error' });
      navigate('/compras/facturas');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadFactura();
  }, [loadFactura]);

  useEffect(() => {
    const st = location.state as { pedirEtiquetas?: boolean; abrirAplicarSaldo?: boolean } | null;
    if (!st || !factura || loading) return;

    const pedirEtiquetas = !!st.pedirEtiquetas;
    const abrirAplicar = !!st.abrirAplicarSaldo;

    if (!pedirEtiquetas && !abrirAplicar) return;

    navigate(location.pathname, { replace: true, state: {} });

    if (abrirAplicar) {
      pedirEtiquetasPendienteRef.current = pedirEtiquetas;
      setAplicarSaldoOpen(true);
      return;
    }

    if (pedirEtiquetas) {
      setEtiquetasOpen(true);
    }
  }, [factura, loading, location.pathname, location.state, navigate]);

  const handleCloseAplicarSaldo = () => {
    setAplicarSaldoOpen(false);
    if (pedirEtiquetasPendienteRef.current) {
      pedirEtiquetasPendienteRef.current = false;
      setEtiquetasOpen(true);
    }
  };

  useEffect(() => {
    pdfEtiquetasRef.current = pdfEtiquetas;
  }, [pdfEtiquetas]);

  useEffect(() => {
    return () => {
      const p = pdfEtiquetasRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas()
      .then(setImpuestoTasas)
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!factura) {
    return null;
  }

  const puedeRegenerarAsiento =
    superAdmin && (factura.importeTotal ?? 0) > 0 && !esFacturaAnulada(factura);

  const handleAnular = async () => {
    if (anulando || esFacturaAnulada(factura)) return;
    const id = factura.nroFormulario;
    try {
      const check = await facturaCompraService.puedeAnularFacturaCompra(id);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular este comprobante', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta factura de compra?')) return;

      setAnulando(true);
      await facturaCompraService.anularFacturaCompra(id);
      enqueueSnackbar('Factura de compra anulada correctamente', { variant: 'success' });
      await loadFactura();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la factura de compra'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  const anulada = esFacturaAnulada(factura);
  const edicionDirty = !anulada && !!edicion && !edicionIgual(edicion, edicionDesdeFactura(factura));

  const edicionNroSuc = Number(edicion?.nroSuc);
  const edicionNroComprobante = Number(edicion?.nroComprobante);
  const edicionErrores = edicion
    ? {
        nroSuc: !Number.isInteger(edicionNroSuc) || edicionNroSuc < 1,
        nroComprobante: !Number.isInteger(edicionNroComprobante) || edicionNroComprobante < 1,
        fechaEmision: !edicion.fechaEmision,
        aplicacionIva: !edicion.aplicacionIva,
        descripcion: edicion.descripcion.length > DESCRIPCION_MAX_LENGTH,
      }
    : null;
  const edicionValida = !!edicionErrores && !Object.values(edicionErrores).some(Boolean);

  const guardarEdicion = async () => {
    if (!edicion || !edicionDirty || guardandoEdicion) return;
    if (!edicionValida) {
      enqueueSnackbar(
        edicionErrores?.descripcion
          ? `Las observaciones no pueden superar los ${DESCRIPCION_MAX_LENGTH} caracteres`
          : 'Revise los datos del comprobante: sucursal, número, fecha de emisión y de aplicación de IVA son obligatorios',
        { variant: 'warning' },
      );
      return;
    }
    setGuardandoEdicion(true);
    try {
      await facturaCompraService.editarDatosComprobante(factura.nroFormulario, {
        nroSuc: edicionNroSuc,
        nroComprobante: edicionNroComprobante,
        fechaEmision: edicion.fechaEmision,
        fechaVencim: edicion.fechaVencim || null,
        aplicacionIva: edicion.aplicacionIva,
        descripcion: edicion.descripcion.trimEnd(),
      });
      const actualizada = await facturaCompraService.getFacturaCompra(factura.nroFormulario);
      setFactura(actualizada);
      setEdicion(edicionDesdeFactura(actualizada));
      enqueueSnackbar('Datos del comprobante actualizados correctamente', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al actualizar los datos del comprobante'), { variant: 'error' });
    } finally {
      setGuardandoEdicion(false);
    }
  };

  const ejecutarRegenerarAsiento = async () => {
    const id = factura.nroFormulario;
    setOpenRegenerarDialog(false);
    setRegenerandoAsiento(true);
    try {
      const r = await facturaCompraService.regenerarAsientoFacturaCompra(id);
      enqueueSnackbar(r.message ?? `Asiento regenerado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      await loadFactura();
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo regenerar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setRegenerandoAsiento(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/facturas')}
                sx={{ textTransform: 'none' }}
              >
                Facturas de Compra
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {String(factura.nroTipoFormulario ?? '').trim() === '2'
                  ? 'Nota de Débito de Compra'
                  : 'Factura de Compra'}{' '}
                {formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {!esFacturaAnulada(factura) && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => setEtiquetasOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Imprimir etiquetas
                </Button>
              )}
              {!esFacturaAnulada(factura) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  disabled={anulando}
                  onClick={() => void handleAnular()}
                >
                  Anular factura
                </Button>
              )}
              {superAdmin && (
                <Tooltip
                  title={
                    puedeRegenerarAsiento
                      ? 'Elimina el/los asiento(s) vinculados y genera uno nuevo con la lógica actual (solo SUPERADMIN).'
                      : 'Se requiere importe total positivo.'
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SyncIcon />}
                      disabled={!puedeRegenerarAsiento || regenerandoAsiento}
                      onClick={() => setOpenRegenerarDialog(true)}
                    >
                      Regenerar asiento
                    </Button>
                  </span>
                </Tooltip>
              )}
              <VerAsientoContableButton
                nroAsiento={factura.nroAsiento}
                sx={{ borderColor: '#1976d2', color: '#1976d2' }}
              />
            </Stack>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {anulada || !edicion || !edicionErrores ? (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Fecha emisión</Typography>
                  <Typography variant="body1">{formatearFecha(factura.fechaEmision)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Nro Comprobante</Typography>
                  <Typography variant="body1">{formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Fecha de vencimiento</Typography>
                  <Typography variant="body1">{formatearFecha(factura.fechaVencim)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">Fecha de aplicación de IVA</Typography>
                  <Typography variant="body1">{formatearFecha(factura.aplicacionIva)}</Typography>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FechaEditableField
                    label="Fecha emisión"
                    required
                    value={edicion.fechaEmision}
                    disabled={guardandoEdicion}
                    onChange={(ymd) => setEdicion({ ...edicion, fechaEmision: ymd })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Letra"
                      value={(factura.letra ?? '').trim() || '-'}
                      disabled
                      sx={{ width: 64, flexShrink: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Sucursal"
                      type="number"
                      required
                      value={edicion.nroSuc}
                      disabled={guardandoEdicion}
                      error={edicionErrores.nroSuc}
                      onChange={(e) => setEdicion({ ...edicion, nroSuc: e.target.value })}
                      inputProps={{ min: 1, step: 1 }}
                      sx={{ width: 96, flexShrink: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Número"
                      type="number"
                      required
                      fullWidth
                      value={edicion.nroComprobante}
                      disabled={guardandoEdicion}
                      error={edicionErrores.nroComprobante}
                      onChange={(e) => setEdicion({ ...edicion, nroComprobante: e.target.value })}
                      inputProps={{ min: 1, step: 1 }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FechaEditableField
                    label="Fecha de vencimiento"
                    value={edicion.fechaVencim}
                    disabled={guardandoEdicion}
                    onChange={(ymd) => setEdicion({ ...edicion, fechaVencim: ymd })}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FechaEditableField
                    label="Fecha de aplicación de IVA"
                    required
                    value={edicion.aplicacionIva}
                    disabled={guardandoEdicion}
                    onChange={(ymd) => setEdicion({ ...edicion, aplicacionIva: ymd })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={6}
                    label="Observaciones"
                    value={edicion.descripcion}
                    disabled={guardandoEdicion}
                    error={edicionErrores.descripcion}
                    onChange={(e) => setEdicion({ ...edicion, descripcion: e.target.value })}
                    inputProps={{ maxLength: DESCRIPCION_MAX_LENGTH }}
                    helperText={`${edicion.descripcion.length}/${DESCRIPCION_MAX_LENGTH}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={guardandoEdicion ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={() => void guardarEdicion()}
                      disabled={!edicionDirty || guardandoEdicion}
                      sx={{ textTransform: 'none' }}
                    >
                      Guardar datos del comprobante
                    </Button>
                    {edicionDirty && !guardandoEdicion && (
                      <Button
                        size="small"
                        onClick={() => setEdicion(edicionDesdeFactura(factura))}
                        sx={{ textTransform: 'none' }}
                      >
                        Descartar cambios
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography variant="body1">{factura.nroProveedor} - {factura.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">CUIT</Typography>
              <Typography variant="body1">{factura.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
              <Typography variant="body1">{factura.formaDePago || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Ubicación destino</Typography>
              <Typography variant="body1">
                {factura.nroUbicacionDestino != null
                  ? factura.ubicacionDestinoDescripcion
                    ? `${factura.nroUbicacionDestino} - ${factura.ubicacionDestinoDescripcion}`
                    : String(factura.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Usuario</Typography>
              <Typography variant="body1">{factura.usuario || '-'}</Typography>
            </Grid>
            {(factura.remitosAsociados?.length ?? 0) > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {(factura.remitosAsociados?.length ?? 0) === 1 ? 'Remito asociado' : 'Remitos asociados'}
                </Typography>
                <Stack spacing={0.5}>
                  {factura.remitosAsociados!.map((r) => (
                    <Link
                      key={r.nroFormulario}
                      component={RouterLink}
                      to={`/compras/remitos/${r.nroFormulario}`}
                      underline="hover"
                      sx={{ color: '#1976d2', fontWeight: 500 }}
                    >
                      {formatoLetraSucNro(r.letra, r.nroSuc, r.nroComprobante ?? undefined)}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            )}
            {(anulada || !edicion || !edicionErrores) && factura.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{factura.descripcion}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cantidad</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio lista</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">% Desc</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio unit.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Descuento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.detalles || []).map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {(d.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'Concepto' : 'Producto'}
                    </TableCell>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioLista ?? 0)}</TableCell>
                    <TableCell align="right">{Number(d.porcDesc)?.toFixed(1) ?? '-'}%</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.descuento ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Totales
          </Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(factura.subtotal ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Descuento
                    {[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                      .filter((p) => p != null && Number(p) > 0).length > 0 && (
                      <> ({[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                        .filter((p) => p != null && Number(p) > 0)
                        .map((p) => `${Number(p).toFixed(1)}%`)
                        .join(', ')})
                      </>
                    )}
                  </Typography>
                  <Typography>{formatCurrency(factura.descuento ?? 0)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal gravado</Typography>
                  <Typography>{formatCurrency(factura.importeSinIva ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa1 != null ? `${impuestoTasas.impuestotasa1}%` : '1'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivainscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa2 != null ? `${impuestoTasas.impuestotasa2}%` : '2'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivanoInscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa3 != null ? `${impuestoTasas.impuestotasa3}%` : '3'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivaotro ?? 0)}</Typography>
                </Stack>
                {(factura.letra ?? '').trim().toUpperCase() === 'X' ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">IVA No categorizado</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Exento</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                )}
                {(factura.percepciones ?? 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      component="span"
                      color="text.secondary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setOpenImpuestosDialog(true)}
                    >
                      Otros impuestos
                      {(factura.impuestos?.length ?? 0) > 0 && (
                        <IconButton size="small" sx={{ ml: 0.5, py: 0 }} onClick={(e) => { e.stopPropagation(); setOpenImpuestosDialog(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                    <Typography>{formatCurrency(factura.percepciones ?? 0)}</Typography>
                  </Stack>
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(factura.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <NumeroInternoMeta
            label="Nro. comprobante"
            valor={formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}
          />
        </CardContent>
      </Card>

      <Dialog open={openRegenerarDialog} onClose={() => setOpenRegenerarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            Se eliminarán todas las líneas del libro diario vinculadas a esta factura (incluido si el asiento estaba
            contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual.
            ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegenerarDialog(false)} disabled={regenerandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={ejecutarRegenerarAsiento} disabled={regenerandoAsiento}>
            {regenerandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openImpuestosDialog} onClose={() => setOpenImpuestosDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Otros impuestos</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto base</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto impuesto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.impuestos ?? []).map((imp, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{imp.codigoImpuesto ?? '-'}</TableCell>
                    <TableCell align="right">{imp.ctaCtable ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoBaseImp ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoImpuesto ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImpuestosDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ImprimirEtiquetasCodigosDialog
        open={etiquetasOpen}
        nroFormulario={factura.nroFormulario}
        detalles={factura.detalles}
        textoOrigen="la factura"
        fileName={`etiquetas-fc-${factura.nroFormulario}.pdf`}
        obtenerPdf={(id, items, idCompania) => facturaCompraService.getEtiquetasCodigosPdf(id, items, idCompania)}
        onClose={() => setEtiquetasOpen(false)}
        onPdfReady={(url, fileName) => {
          setPdfEtiquetas((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return { url, fileName };
          });
        }}
      />

      {(factura.nroProveedor ?? 0) > 0 && (
        <AplicarSaldoReciboDialog
          open={aplicarSaldoOpen}
          onClose={handleCloseAplicarSaldo}
          variant="proveedor"
          nroCliente={Number(factura.nroProveedor)}
          nombreCliente={factura.nombre || `Proveedor ${factura.nroProveedor}`}
          selectorMode
        />
      )}

      {pdfEtiquetas ? (
        <PDFViewer
          pdfUrl={pdfEtiquetas.url}
          title={`Etiquetas factura ${formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}`}
          documentId={String(factura.nroFormulario)}
          documentType="informeCliente"
          downloadFileName={pdfEtiquetas.fileName}
          onClose={() => {
            URL.revokeObjectURL(pdfEtiquetas.url);
            setPdfEtiquetas(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default FacturaCompraDetalle;
